// src/pages/Dashboard.jsx

import React, { useEffect } from 'react';
import {
  NavLink,
  Outlet,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Headers from '../components/Headers';
import Footer from '../components/Footer';

import { RxDashboard }        from 'react-icons/rx';
import { RiProductHuntLine } from 'react-icons/ri';
import { BsHeart, BsChat }   from 'react-icons/bs';
import { TfiLock }           from 'react-icons/tfi';
import { BiLogInCircle }     from 'react-icons/bi';
import { FiChevronRight }    from 'react-icons/fi';
import { MdLocalShipping }   from 'react-icons/md';

import axios from 'axios';
import { base_url } from '../utils/config';
import { user_reset }  from '../store/reducers/authReducer';
import { reset_count } from '../store/reducers/cardReducer';
import { fetchProfile } from '../store/reducers/profileSlice';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userInfo } = useSelector((state) => state.auth);
  const profile = useSelector((state) => state.profile.user);
  const profileStatus = useSelector((state) => state.profile.status);

  const isRoot = location.pathname === '/dashboard';

  useEffect(() => {
    if (isRoot) dispatch(fetchProfile());
  }, [dispatch, isRoot]);

  const logout = async () => {
    try {
      await axios.get(
        `${base_url}/api/customer/logout`,
        { withCredentials: true }
      );
      localStorage.removeItem('customerToken');
      dispatch(user_reset());
      dispatch(reset_count());
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const displayName =
    profileStatus === 'succeeded' && profile?.name
      ? profile.name
      : userInfo?.name || 'Customer';

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #6366F1, #EC4899);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #4F46E5, #DB2777);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #6366F1 transparent;
        }
      `}</style>

      <Headers />

      <div className="bg-slate-200 mt-5 min-h-[80vh]">
        <div className="container mx-auto px-4 py-6">
          {/* Top nav */}
          <nav className="bg-white rounded-md shadow p-4 mb-6 overflow-x-auto scroll-smooth custom-scrollbar">
            <ul className="flex flex-nowrap space-x-6 whitespace-nowrap">
              {[
                { to: '/dashboard',            label: 'Dashboard',     Icon: RxDashboard,       end: true },
                { to: '/dashboard/my-orders',   label: 'My Orders',     Icon: RiProductHuntLine },
                { to: '/dashboard/hyper-delivery', label: 'Deliveries', Icon: MdLocalShipping },
                { to: '/dashboard/my-wishlist', label: 'Wishlist',      Icon: BsHeart },
                { to: '/dashboard/chat',        label: 'Chat',          Icon: BsChat },
                { to: '/dashboard/change-password', label: 'Change Password', Icon: TfiLock }
              ].map(({ to, label, Icon, end }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      `flex items-center gap-2 text-slate-700 hover:text-indigo-600 ${
                        isActive ? 'text-indigo-600 font-bold' : ''
                      }`
                    }
                  >
                    <Icon className="text-xl" />
                    <span>{label}</span>
                  </NavLink>
                </li>
              ))}

              <li onClick={logout}>
                <div className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 cursor-pointer">
                  <BiLogInCircle className="text-xl" />
                  <span>Logout</span>
                </div>
              </li>
            </ul>
          </nav>

          {/* Main content */}
          {isRoot ? (
            <div className="space-y-6">
              {/* Profile card */}
              <div className="bg-white rounded-md p-4 flex justify-between items-center">
                <h2 className="text-lg font-medium">{displayName}</h2>
                <NavLink
                  to="/dashboard/complete-profile"
                  className="bg-red-500 text-white px-3 py-1 rounded-md"
                >
                  Complete Profile
                </NavLink>
              </div>

              {/* My Addresses */}
              <div className="space-y-2">
                <h3 className="text-slate-700 font-semibold">My Addresses</h3>
                <NavLink
                  to="/dashboard/add-address"
                  className="block bg-white rounded-md shadow p-4 flex justify-between items-center"
                >
                  <span>Add an Address</span>
                  <FiChevronRight />
                </NavLink>
              </div>

              {/* Other Actions */}
              <div className="space-y-2">
                <h3 className="text-slate-700 font-semibold">Other Actions</h3>
                <ul className="bg-white rounded-md shadow divide-y">
                  <li className="flex justify-between items-center p-4">
                    <span>Order History</span>
                    <FiChevronRight />
                  </li>
                  <li className="p-4">
                    <NavLink
                      to="/dashboard/hyper-delivery"
                      className="flex justify-between items-center w-full"
                    >
                      <span>Deliveries</span>
                      <div className="flex items-center gap-2">
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          1
                        </span>
                        <FiChevronRight />
                      </div>
                    </NavLink>
                  </li>
                  <li className="flex justify-between items-center p-4">
                    <span>Contact Us</span>
                    <FiChevronRight />
                  </li>
                  <li className="flex justify-between items-center p-4">
                    <span>Share App</span>
                    <FiChevronRight />
                  </li>
                  <li className="flex justify-between items-center p-4">
                    <NavLink
                      to="/aboutus"
                      className="flex justify-between items-center w-full"
                    >
                      <span>About</span>
                      <FiChevronRight />
                    </NavLink>
                  </li>
                  <li className="flex justify-between items-center p-4">
                    <span>Account Settings</span>
                    <FiChevronRight />
                  </li>
                  <li
                    onClick={logout}
                    className="flex justify-between items-center p-4 cursor-pointer"
                  >
                    <span>Logout</span>
                    <FiChevronRight />
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Dashboard;
